import type { RoleValue } from '@features/users/components/detail-panel/UserDetailPanel.types';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { UserFormFieldErrors } from '@features/users/components/detail-panel/components/user-form-fields/UserFormFields.types';
import { UserFormData } from '@features/users/components/detail-panel/UserDetailPanel.types';
import {
  computeRolesAfterToggle,
  getInitialFormData,
  hasFormChanges,
  sanitizeFormData,
  serializeRolesForApi,
} from '@features/users/components/detail-panel/UserDetailPanel.utils';
import * as React from 'react';

import {
  createUserSchema,
  editUserSchema,
} from '../../schemas/userDetailPanel.schemas';
import { useCheckUserEmail } from '../use-check-user-email/useCheckUserEmail';
import { useCreateUser } from '../use-create-user/useCreateUser';
import { useUpdateUser } from '../use-update-user/useUpdateUser';
import { useUser } from '../use-user/useUser';
import {
  UseUserDetailPanelParams,
  UseUserDetailPanelReturn,
} from './useUserDetailPanel.types';

export function useUserDetailPanel({
  mode,
  userId,
  initialData,
  onClose,
}: UseUserDetailPanelParams): UseUserDetailPanelReturn {
  const { email: authenticatedUserEmail } = useAuthenticatedUser();
  const { selectedOrganization } = useOrganizations();
  const organizationId = selectedOrganization?.id ?? '';

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserFetchError,
  } = useUser({
    organizationId,
    userId: userId ?? '',
  });

  const createMutation = useCreateUser({ organizationId });
  const updateMutation = useUpdateUser({ organizationId });

  const initialFormData = React.useMemo(
    () => getInitialFormData(mode, user, initialData),
    [mode, user, initialData]
  );

  const [formData, setFormData] = React.useState<UserFormData>(initialFormData);
  const [errors, setErrors] = React.useState<UserFormFieldErrors>({});
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);
  const [apiError, setApiError] = React.useState(false);

  const {
    isCheckingEmail,
    emailExistsError,
    handleEmailBlur,
    handleEmailFocus,
    resetEmailCheck,
  } = useCheckUserEmail(formData.email, {
    organizationId,
    enabled: mode === 'add',
  });

  const isAddMode = mode === 'add' || mode === 'sf-add';

  React.useEffect(() => {
    setFormData(initialFormData);
    setErrors({});
    setApiError(false);
    resetEmailCheck();
  }, [initialFormData, resetEmailCheck]);

  const handleFieldChange = React.useCallback(
    (field: keyof UserFormData, value: string): void => {
      setFormData(prev => ({ ...prev, [field]: value }));
      setErrors(prev => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleRoleToggle = React.useCallback((role: RoleValue): void => {
    setFormData(prev => ({
      ...prev,
      roles: computeRolesAfterToggle(prev.roles, role),
    }));
    setErrors(prev => ({ ...prev, roles: undefined }));
  }, []);

  const handleStatusChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const status = event.target.checked ? 'Active' : 'Inactive';
      setFormData(prev => ({ ...prev, status }));
    },
    []
  );

  const handleCloseAttempt = React.useCallback((): void => {
    if (mode === 'view') {
      onClose();
      return;
    }

    if (hasFormChanges(formData, initialFormData)) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  }, [mode, formData, initialFormData, onClose]);

  const handleKeepEditing = React.useCallback((): void => {
    setShowUnsavedModal(false);
  }, []);

  const handleExitDiscard = React.useCallback((): void => {
    setShowUnsavedModal(false);
    onClose();
  }, [onClose]);

  const submitForm = React.useCallback((): void => {
    const sanitized = sanitizeFormData(formData);
    setApiError(false);

    if (isAddMode) {
      createMutation.mutate(
        {
          firstName: sanitized.firstName,
          middleName: sanitized.middleName || null,
          lastName: sanitized.lastName,
          email: sanitized.email,
          roles: serializeRolesForApi(sanitized.roles),
        },
        {
          onSuccess: (): void => {
            emitFlagMessage('success', 'User created successfully!', 5000);
            onClose();
          },
          onError: (): void => {
            setApiError(true);
          },
        }
      );
    } else if (mode === 'edit' && userId) {
      updateMutation.mutate(
        {
          id: userId,
          firstName: sanitized.firstName,
          middleName: sanitized.middleName || null,
          lastName: sanitized.lastName,
          email: sanitized.email,
          roles: serializeRolesForApi(sanitized.roles),
          status: sanitized.status,
        },
        {
          onSuccess: (): void => {
            emitFlagMessage('success', 'User updated successfully!', 5000);
            onClose();
          },
          onError: (): void => {
            setApiError(true);
          },
        }
      );
    }
  }, [
    mode,
    formData,
    userId,
    isAddMode,
    createMutation,
    updateMutation,
    onClose,
  ]);

  const handleSubmit = React.useCallback((): void => {
    const schema = isAddMode ? createUserSchema : editUserSchema;
    const result = schema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: UserFormFieldErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof UserFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    if (emailExistsError) {
      setErrors(prev => ({ ...prev, email: emailExistsError }));
      return;
    }

    if (isCheckingEmail) {
      return;
    }

    submitForm();
  }, [formData, isAddMode, submitForm, emailExistsError, isCheckingEmail]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isEditOrView = mode === 'edit' || mode === 'view';
  const isSelfUser = formData.email !== undefined && authenticatedUserEmail !== undefined && formData.email.toLowerCase() === authenticatedUserEmail.toLowerCase();
  const isLoadingUser = isEditOrView && isUserLoading;
  const isUserError = isEditOrView && isUserFetchError;
  const panelTitle = isAddMode ? 'Create new user' : 'Edit user';

  const mergedErrors = React.useMemo((): UserFormFieldErrors => {
    if (emailExistsError && !errors.email) {
      return { ...errors, email: emailExistsError };
    }
    return errors;
  }, [errors, emailExistsError]);

  return {
    formData,
    errors: mergedErrors,
    apiError,
    showUnsavedModal,
    isSubmitting,
    isEditOrView,
    isSelfUser,
    isLoadingUser,
    isUserError,
    panelTitle,
    isCheckingEmail,
    handleFieldChange,
    handleRoleToggle,
    handleStatusChange,
    handleCloseAttempt,
    handleKeepEditing,
    handleExitDiscard,
    handleSubmit,
    handleEmailBlur,
    handleEmailFocus,
  };
}
